import {
  ChartInsight,
  isCatalystInsight,
  isEarningsInsight,
  isInsightFilter,
} from '@api/api/insights';
import { getConditionIcons } from '@terminal/components/insight-icons/utils';
import {
  InsightIconsRoot,
  InsightIconsRootProps,
} from '@terminal/components/insight-icons-root/InsightIconsRoot';
import { InsightsTooltip } from '@terminal/components/insights-tooltip/InsightsTooltip';
import { useInsightDrawer } from '@terminal/hooks/use-insight-drawer/useInsightDrawer';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import insightIconStyles from '@terminal/views/dashboard/my-assets-widget/assets-table/insights-cell/insights-icon/InsightIcon.module.scss';
import { getExploreDirection } from '@terminal/views/dashboard/whats-new-widget/components/insight-card/utils';
import { CatalystInsightIcon } from '@terminal/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';
import { isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

const ICON_W = 24;
const ICON_H = 28;
const VERTICAL_COMBINED_H = 47;
const SMALL_ICON_W = 12;
const SMALL_ICON_H = 14;
const SMALL_VERTICAL_COMBINED_H = 24;

export interface ChartV2InsightIconProps {
  insight: ChartInsight;
  small?: boolean;
  source?: PosthogTrackingSource;
}

/**
 * Renders one `ChartInsight` as a self-contained badge with its own tooltip
 * and click handler. No absolute-positioning math — chartv2's
 * `<InsightsOverlay>` handles placement at the right x for each time group.
 */
export const ChartV2InsightIcon = ({
  insight,
  small,
  source = POSTHOG_TRACKING_SOURCES.CHART,
}: ChartV2InsightIconProps) => {
  const { t } = useTranslation(['chart']);
  const { openDrawer: openInsightDrawer } = useInsightDrawer();

  const iconWidth = small ? SMALL_ICON_W : ICON_W;
  const singleIconHeight = small ? SMALL_ICON_H : ICON_H;
  const verticalCombinedHeight = small
    ? SMALL_VERTICAL_COMBINED_H
    : VERTICAL_COMBINED_H;

  if (isCatalystInsight(insight)) {
    return (
      <CatalystInsightIcon
        catalystInsightId={insight.id}
        subCategory={insight.sub_category}
        customSize={{
          width: iconWidth,
          height: singleIconHeight,
        }}
        isBordered
        identifier={insight.identifier}
        source={source}
        category={insight.category}
      />
    );
  }

  if (isEarningsInsight(insight)) {
    return (
      <EarningsInsightIcon
        entityTag={insight.entity_tag}
        earningsInsight={insight}
        customSize={{
          width: iconWidth,
          height: singleIconHeight,
        }}
        isBordered
        source={source}
      />
    );
  }

  if (!isInsightFilter(insight)) {
    return null;
  }

  const isStarred = isToday(new Date(insight.created_at));
  const conditions = getConditionIcons({
    kind: insight.metadata.kind,
    conditions: insight.metadata.conditions,
  });
  const isCombined = conditions.length > 1;
  const iconHeight = isCombined ? verticalCombinedHeight : singleIconHeight;
  const iconSize = {
    width: `${iconWidth}px`,
    height: `${iconHeight}px`,
  };

  const episodeLabel = t('chart:insightCard.episode', {
    count: insight.metadata.episode_count,
  });

  const handleScenarioClick = () => {
    openInsightDrawer({
      id: insight.id,
      entityTag: insight.metadata.entity,
      trackingProperties: { source },
    });
  };

  const insightTooltipProps: InsightIconsRootProps = {
    conditions,
    direction: getExploreDirection(insight.metadata.direction),
    customSize: iconSize,
    isStarred,
    isCombined,
    layout: 'vertical',
    isSparklesCondensed: small,
  };

  return (
    <InsightsTooltip
      horizon={insight.metadata.horizon}
      median={insight.metadata.median_return}
      stars={insight.metadata.stars}
      name={insight.card.name}
      entityTag={insight.metadata.entity}
      episodeLabel={episodeLabel}
      {...insightTooltipProps}
    >
      <div className={insightIconStyles.scenarioIconWrapper}>
        <InsightIconsRoot
          {...insightTooltipProps}
          onIconClick={handleScenarioClick}
          source={source}
          insightId={insight.id}
          entities={
            insight.metadata.entity ? [insight.metadata.entity] : undefined
          }
        />
      </div>
    </InsightsTooltip>
  );
};
